import { anchors } from './anchorNames';

/**
 * The cards to fly across the screen for what changed on one player's side between two snapshots (snapshot), in
 * order: each { from, to, card } names two places (anchors) and the card to show on the way (null for face down).
 * An energy card `becomesSymbol`: it shrinks into its energy symbol on the Pokémon rather than landing on top.
 *
 * The game sends where every card is now, not how it got there, so the moves are read from the difference: a Pokémon
 * that left a bench spot and is now active moved up; the hand growing as the deck shrinks is a draw; a Pokémon with
 * one more energy card had it attached; a Pokémon gone from play and a bigger discard pile is a knockout. Anything
 * new in play that came from nowhere else came from the hand.
 */
export function cardMoves(prev, next) {
  if (!prev || !next || prev.userId !== next.userId) {
    return [];
  }

  const userId = next.userId;
  const moves = [];
  const fly = (from, to, card, becomesSymbol = false) => moves.push({ from, to, card: card || null, becomesSymbol });
  const benchPositionOf = (side, cardId) => side.bench.findKey(spot => spot && spot.cardId === cardId);
  // The hand card that left: with two of the same card in hand, the one no longer there, not just the first.
  const stillInHand = new Set(next.handCards.map(({ pivotId }) => pivotId));
  const taken = new Set();
  const fromHand = (cardId) => {
    const card = prev.handCards.find(({ card: handCard, pivotId }) => handCard.get('id') === cardId
      && !stillInHand.has(pivotId) && !taken.has(pivotId));
    if (!card) {
      return anchors.hand(userId);
    }
    taken.add(card.pivotId);
    return anchors.handCard(userId, card.pivotId);
  };

  // The active spot: a benched Pokémon moving up, an evolution, or the first one out.
  const movedUp = new Set();
  const activeChanged = next.active && (!prev.active || prev.active.rowId !== next.active.rowId
    || prev.active.cardId !== next.active.cardId);
  if (activeChanged && next.active.cardId && !prev.activeFaceDown) {
    const benchPosition = benchPositionOf(prev, next.active.cardId);
    const sameRow = prev.active && prev.active.rowId === next.active.rowId;

    if (sameRow && prev.active.cardId) {
      fly(fromHand(next.active.cardId), anchors.active(userId), next.active.card);
    } else if (benchPosition !== undefined && (next.bench.get(benchPosition) || {}).cardId !== next.active.cardId) {
      movedUp.add(benchPosition);
      fly(anchors.bench(userId, benchPosition), anchors.active(userId), next.active.card);
    } else if (!sameRow) {
      fly(fromHand(next.active.cardId), anchors.active(userId), next.active.card);
    }
  }

  // The old active Pokémon: back to the bench (retreat, Switch) or gone (knocked out).
  const left = prev.active && (!next.active || next.active.rowId !== prev.active.rowId) ? prev.active : null;
  const movedDown = new Set();
  if (left && left.cardId) {
    const benchPosition = next.bench.findKey((spot, position) => spot && spot.cardId === left.cardId
      && (prev.bench.get(position) || {}).cardId !== left.cardId);
    if (benchPosition !== undefined) {
      movedDown.add(benchPosition);
      fly(anchors.active(userId), anchors.bench(userId, benchPosition), left.card);
    } else if (next.discardCount > prev.discardCount) {
      fly(anchors.active(userId), anchors.discard(userId), left.card);
    }
  }

  // Bench spots: played from the hand, evolved, or gone.
  next.bench.forEach((spot, position) => {
    const was = prev.bench.get(position);
    if (!spot || !spot.cardId || movedDown.has(position)) {
      return;
    }
    if (!was) {
      fly(fromHand(spot.cardId), anchors.bench(userId, position), spot.card);
    } else if (was.rowId === spot.rowId && was.cardId && was.cardId !== spot.cardId) {
      fly(fromHand(spot.cardId), anchors.bench(userId, position), spot.card);
    }
  });
  prev.bench.forEach((was, position) => {
    if (was && was.cardId && !next.bench.get(position) && !movedUp.has(position)) {
      fly(anchors.bench(userId, position), anchors.discard(userId), was.card);
    }
  });

  // Energy attached to a Pokémon that stayed where it was.
  const spots = [[prev.active, next.active, anchors.active(userId)]]
    .concat(next.bench.map((spot, position) => [prev.bench.get(position), spot, anchors.bench(userId, position)]).valueSeq().toArray());
  spots.forEach(([was, now, place]) => {
    if (was && now && was.rowId === now.rowId && now.energy.size > was.energy.size) {
      now.energy.skip(was.energy.size).forEach(card => fly(fromHand(card && card.get('id')), place, card, true));
    }
  });

  // The deal: the deck goes down and the hand and prizes come off it.
  if (prev.deckCount === 0 && prev.handCount === 0 && next.deckCount > 0) {
    next.handCards.forEach(({ pivotId, card }) => fly(anchors.deck(userId), anchors.handCard(userId, pivotId), card));
    if (!next.handCards.size) {
      for (let i = 0; i < next.handCount; i++) {
        fly(anchors.deck(userId), anchors.hand(userId), null);
      }
    }
    next.prizes.sort().forEach(position => fly(anchors.deck(userId), anchors.prize(userId, position), null));
    return moves;
  }

  // Prize cards taken, then cards drawn from the deck: everything that left the deck, except into the prizes
  // (setting up), went to the hand. Your own land on their new places in the hand; the opponent's go face down to
  // their hand.
  prev.prizes.subtract(next.prizes).sort().forEach(position => fly(anchors.prize(userId, position), anchors.hand(userId), null));
  const newInHand = next.handCards.filter(({ pivotId }) => !prev.handCards.some(old => old.pivotId === pivotId));
  const leftDeck = prev.deckCount - next.deckCount - Math.max(0, next.prizes.size - prev.prizes.size);
  const drawn = next.handCards.size || prev.handCards.size ? Math.min(leftDeck, newInHand.size) : leftDeck;
  for (let i = 0; i < drawn; i++) {
    const card = newInHand.get(i);
    fly(anchors.deck(userId), card ? anchors.handCard(userId, card.pivotId) : anchors.hand(userId), card ? card.card : null);
  }

  // A Trainer played from the hand lands face up on the discard pile.
  const top = next.discardTop;
  const isTrainer = top && top.get('trainer_steps') !== undefined && top.get('trainer_steps') !== null;
  if (isTrainer && (!prev.discardTop || prev.discardTop.get('pivot_id') !== top.get('pivot_id'))) {
    fly(fromHand(top.get('id')), anchors.discard(userId), top);
  }

  return moves.filter(move => move.from !== move.to);
}
